import React from 'react';
import { AssetTransactionContext, AssetTransactionContextProps } from '../context/AssetTransactionContext';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { AssetTransaction } from '../types/asset_transaction/assetTransaction';

const assetTransactionState = {
  items: new Map<string, AssetTransaction>(),
  loading: false,
};

export const assetTransactionSlice = createSlice({
  name: 'assetTransaction',
  initialState: { key: v4() },
  reducers: {
    reset: state => {
      assetTransactionState.items = new Map();
      assetTransactionState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<AssetTransaction[]>) => {
      assetTransactionState.items = new Map();
      action.payload.forEach(p => assetTransactionState.items.set(p.id, new AssetTransaction(p)));
      state.key = v4();
    },
    add: (state, action: PayloadAction<AssetTransaction[]>) => {
      action.payload.forEach(p => {
        const existing = assetTransactionState.items.get(p.id);
        if (!existing || p.version >= existing.version) assetTransactionState.items.set(p.id, new AssetTransaction(p));
      });
      assetTransactionState.items = new Map(assetTransactionState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<AssetTransaction[]>) => {
      action.payload.forEach(p => assetTransactionState.items.delete(p.id));
      assetTransactionState.items = new Map(assetTransactionState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      assetTransactionState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface AssetTransactionProviderProps {
  children: React.ReactNode;
}

export default function AssetTransactionProvider({ children }: AssetTransactionProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.assetTransactions.key);

  const assetTransactionContext: AssetTransactionContextProps = {
    assetTransactions: assetTransactionState.items,
    assetTransactionsLoading: assetTransactionState.loading,
    setAssetTransactions: (at: Map<string, AssetTransaction>) => {
      dispatch(assetTransactionSlice.actions.set([...at.values()]));
    },
  };

  return (
    <AssetTransactionContext.Provider value={assetTransactionContext}>{children}</AssetTransactionContext.Provider>
  );
}
